import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { openImpersonationInNewTab } from "@/lib/impersonation";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    MagnifyingGlass, Phone, Envelope, Plus, X, ArrowRight, CaretDown,
    ShieldStar, Crown, Users, Heartbeat, Factory, Strategy, Headset,
    ClipboardText, ShieldCheck, ChalkboardTeacher, ListBullets, SquaresFour, Circle,
    SignIn, VideoCamera, Wrench, CheckSquare, Square,
    Upload, DownloadSimple, DotsThreeVertical, CaretLeft, CaretRight,
    UserMinus, UserPlus, ShieldWarning, FileArrowUp, FileArrowDown,
} from "@phosphor-icons/react";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import { CommentRequiredDialog } from "@/components/admin/CommentRequiredDialog";


/* iter101.40 — Strip the legacy "CoE-" / "COE-" prefix (still baked into
   BUILT_IN_ROLES on the backend) so the roster reads clean: e.g.
   "CoE-Patient Engagement Specialists" → "Patient Engagement Specialists". */
function stripCoePrefix(label) {
    if (!label) return "";
    return String(label).replace(/^co[eE]-\s*/i, "").trim();
}

/* ============================================================================
   CoePeopleTab — list every COE member with role + workload + status, plus
   an add/edit dialog gated by `coe_team.create | coe_team.edit` permissions.
   Supports list and grid layouts, and accepts an `addSignal` prop from the
   parent page header to open the Add-User dialog from outside this tab.
   ========================================================================== */

const EMPTY = { name: "", phone: "", email: "", staff_type: "counsellor", custom_role_id: "" };
const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const LS_VIEW_KEY = "coe_people_view";

/* Icon + accent per staff_type — drives the COE breakdown cards. */
const STAFF_TYPE_VIS = {
    counsellor:     { Icon: Heartbeat,           tone: "sky" },
    planner:        { Icon: ClipboardText,       tone: "violet" },
    qa:             { Icon: ShieldCheck,         tone: "emerald" },
    production:     { Icon: Factory,             tone: "amber" },
    technician:     { Icon: Wrench,              tone: "amber" },
    sales:          { Icon: Strategy,            tone: "rose" },
    support:        { Icon: Headset,             tone: "sky" },
    coe_lead:       { Icon: ChalkboardTeacher,   tone: "primary" },
    course_creator: { Icon: VideoCamera,         tone: "violet" },
};

const TONE_CLS = {
    sky:     { bg: "bg-sky-100",     text: "text-sky-700" },
    violet:  { bg: "bg-violet-100",  text: "text-violet-700" },
    emerald: { bg: "bg-emerald-100", text: "text-emerald-700" },
    amber:   { bg: "bg-amber-100",   text: "text-amber-700" },
    rose:    { bg: "bg-rose-100",    text: "text-rose-700" },
    primary: { bg: "bg-primary/10",  text: "text-primary" },
};

export function CoePeopleTab({ addSignal = 0, initialFilter = null } = {}) {
    const { user: currentUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [roles, setRoles] = useState([]);
    const [overview, setOverview] = useState(null);
    const [onlineIds, setOnlineIds] = useState(new Set());
    const [q, setQ] = useState("");
    /* Legacy `filter` state — still driven by the staff_type breakdown cards. */
    const [filter, setFilter] = useState(initialFilter || "all");

    /* iter101.34 — Status chip synced with `?status=` URL param so the
       page-level KPI tile clicks (Online / Disabled) land on the right
       pre-filter. */
    const statusFromUrl = searchParams.get("status") || "all";
    const [statusChip, setStatusChipInternal] = useState(statusFromUrl);
    useEffect(() => { setStatusChipInternal(statusFromUrl); }, [statusFromUrl]);
    const setStatusChip = (val) => {
        setStatusChipInternal(val);
        const next = new URLSearchParams(searchParams);
        if (val === "all") next.delete("status");
        else next.set("status", val);
        setSearchParams(next, { replace: true });
    };

    /* iter101.34 — Team / Role dropdown filters live in the FilterSearchBar
       rightSlot. Team is `staff_type`; Role is a derived permission tier. */
    const [teamFilter, setTeamFilter] = useState("all");
    const [roleFilter, setRoleFilter] = useState("all");

    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    // DEV_MODE flag — gates the "Test login as this user" button. Fetched
    // from /auth/config so we never accidentally ship the impersonate button
    // to production builds.
    const [devMode, setDevMode] = useState(false);
    /* Layout — persisted in localStorage so the operator's choice sticks
       across reloads. List is the default for dense scanning. */
    const [view, setView] = useState(() => {
        try { return localStorage.getItem(LS_VIEW_KEY) || "list"; } catch { return "list"; }
    });

    /* iter101.34 — Bulk-select state (D). Selected IDs is a Set so we can
       toggle in O(1). Clears whenever filters change so we never
       accidentally act on an off-screen selection. */
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkRoleId, setBulkRoleId] = useState("");

    /* iter101.34 — Import CSV drop-zone dialog (E). */
    const [importOpen, setImportOpen] = useState(false);

    /* iter101.34 — Client-side pagination (F). Server-side is overkill for
       the 500-user cap; slicing in-memory keeps filters + search snappy. */
    const PAGE_SIZE = 25;
    const [page, setPage] = useState(1);

    const setViewPersisted = (v) => {
        setView(v);
        try { localStorage.setItem(LS_VIEW_KEY, v); } catch { /* noop */ }
    };

    const load = () => {
        api.get("/admin/coe/people").then((r) => setItems(r.data || [])).catch(() => {});
        api.get("/admin/coe/roles").then((r) => setRoles(r.data || [])).catch(() => {});
        api.get("/admin/coe/overview").then((r) => setOverview(r.data)).catch(() => {});
        // /auth/config is unauthenticated and returns { dev_mode: bool, ... }
        api.get("/auth/config").then((r) => setDevMode(!!r.data?.dev_mode)).catch(() => {});
        // Pull the online-members list from heartbeat so we can put a
        // green dot next to whoever's live right now.
        api.get("/admin/coe/heartbeat").then((r) => {
            const ids = new Set((r.data?.online_members || []).map((m) => m.id));
            setOnlineIds(ids);
        }).catch(() => {});
    };
    useEffect(load, []);

    /* React to the header "Add User" button. The initial mount value (0)
       is ignored — only subsequent increments trigger the dialog. */
    useEffect(() => {
        if (addSignal > 0) { setForm(EMPTY); setOpen(true); }
    }, [addSignal]);

    /* Reset page + clear selection whenever the filter surface changes. */
    useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [q, filter, statusChip, teamFilter, roleFilter]);

    const stats = useMemo(() => {
        const total = items.length;
        const active = items.filter((p) => p.is_active !== false).length;
        const disabled = total - active;
        const admins = items.filter((p) => p.role === "admin").length;
        return { total, active, disabled, admins };
    }, [items]);

    /* iter101.34 — Derive the "Admin / Editor / Viewer" permission tier
       from the user record. Admin is authoritative; a custom_role means
       Editor (can edit within their permissions); everyone else is
       Viewer. This mirrors the mockup's Role column semantics and needs
       no backend change. */
    const roleTierOf = (p) => {
        if (p.role === "admin") return "admin";
        if (p.custom_role_id) return "editor";
        return "viewer";
    };

    const filtered = useMemo(() => {
        let list = items;
        // Status chip
        if (statusChip === "active") list = list.filter((p) => p.is_active !== false);
        else if (statusChip === "disabled") list = list.filter((p) => p.is_active === false);
        // Team dropdown
        if (teamFilter !== "all") list = list.filter((p) => p.staff_type === teamFilter);
        // Role dropdown
        if (roleFilter !== "all") list = list.filter((p) => roleTierOf(p) === roleFilter);
        // Legacy staff_type / status card filter
        if (filter === "active") list = list.filter((p) => p.is_active !== false);
        else if (filter === "disabled") list = list.filter((p) => p.is_active === false);
        else if (filter === "admins") list = list.filter((p) => p.role === "admin");
        else if (filter !== "all") list = list.filter((p) => p.staff_type === filter);
        // Search
        if (q.trim()) {
            const ql = q.toLowerCase();
            list = list.filter((p) =>
                (p.name || "").toLowerCase().includes(ql) ||
                (p.phone || "").includes(q) ||
                (p.email || "").toLowerCase().includes(ql) ||
                (p.role_label || "").toLowerCase().includes(ql)
            );
        }
        return list;
    }, [items, q, filter, statusChip, teamFilter, roleFilter]);

    /* Pagination slice */
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const pageSlice = useMemo(
        () => filtered.slice(pageStart, pageStart + PAGE_SIZE),
        [filtered, pageStart]
    );

    /* Max open-lead / open-case counts across the visible slice — drive
       the bar-width scale for the WorkloadInline component (iter101.24). */
    const maxOpenLeads = useMemo(
        () => Math.max(1, ...pageSlice.map((p) => p.open_leads_count || 0)),
        [pageSlice]
    );
    const maxOpenCases = useMemo(
        () => Math.max(1, ...pageSlice.map((p) => p.open_cases_count || 0)),
        [pageSlice]
    );

    /* Team dropdown options — union of staff_types from the overview
       endpoint (which knows the pretty labels) and the actual roster
       (so we never miss a rare staff_type). */
    const teamOptions = useMemo(() => {
        const map = new Map();
        (overview?.by_staff_type || []).forEach((t) => {
            map.set(t.key, t.short_label || t.label || t.key);
        });
        items.forEach((p) => {
            if (p.staff_type && !map.has(p.staff_type)) {
                map.set(p.staff_type, p.role_label || p.staff_type);
            }
        });
        return Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [items, overview]);

    const openCreate = () => { setForm(EMPTY); setOpen(true); };

    const save = async () => {
        if (!form.name.trim() || !isValid10Digit(form.phone)) {
            toast.error("Name and a 10-digit Indian mobile are required");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, phone: toE164(form.phone), custom_role_id: form.custom_role_id || null };
            await api.post("/admin/coe/people", payload);
            toast.success("Created");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        }
        setSaving(false);
    };

    /* iter101.34 — Bulk-select helpers */
    const isAllSelected = pageSlice.length > 0 && pageSlice.every((p) => selectedIds.has(p.id));
    const toggleSelectAll = () => {
        const next = new Set(selectedIds);
        if (isAllSelected) pageSlice.forEach((p) => next.delete(p.id));
        else pageSlice.forEach((p) => next.add(p.id));
        setSelectedIds(next);
    };
    const toggleSelect = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    };
    const clearSelection = () => setSelectedIds(new Set());

    /* iter101.34 — Bulk-action execution. Backend endpoint accepts one of
       "deactivate" | "activate" | "change_role" and returns a summary
       with per-user status so the toast can be honest about partial
       failures (e.g. tried to deactivate yourself). */
    const executeBulk = async (action, payload = {}) => {
        if (selectedIds.size === 0) return;
        setBulkBusy(true);
        try {
            const r = await api.post("/admin/coe/people/bulk", {
                user_ids: Array.from(selectedIds),
                action,
                ...payload,
            });
            const applied = r.data?.applied || 0;
            const skipped = r.data?.skipped || 0;
            if (skipped > 0) {
                toast.success(`${applied} applied · ${skipped} skipped`);
            } else {
                toast.success(`${action.replace("_", " ")} applied to ${applied} user${applied === 1 ? "" : "s"}`);
            }
            clearSelection();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk action failed");
        }
        setBulkBusy(false);
    };

    /* iter101.34 — Export current filtered roster as CSV. Query params
       mirror the filters so the download matches what the admin sees. */
    const exportCsv = async () => {
        const params = new URLSearchParams();
        if (statusChip !== "all") params.set("status", statusChip);
        if (teamFilter !== "all") params.set("team", teamFilter);
        if (roleFilter !== "all") params.set("role", roleFilter);
        if (q.trim()) params.set("q", q.trim());
        try {
            const r = await api.get(`/admin/coe/people-export?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `coe-users-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("CSV exported");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="coe-people-tab">
            {/* iter101.34 — Canonical search bar with 3 dropdowns + list/grid
                toggle. Layout inspired by the mockup, executed with our
                existing FilterSearchBar primitive so the grammar stays.
                (iter101.35 — the 9-card "Lab & Ops Team breakdown" grid
                that used to live above this bar was removed; the Team
                dropdown in the rightSlot below now serves that role.) */}
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search by name, phone, email, role…"
                searchTestid="coe-people-search"
                testid="coe-people-toolbar"
                rightSlot={(
                    <>
                        <FilterSelect
                            value={teamFilter}
                            onChange={setTeamFilter}
                            testid="coe-people-team-filter"
                            options={[{ value: "all", label: "All Teams" }, ...teamOptions]}
                        />
                        <FilterSelect
                            value={roleFilter}
                            onChange={setRoleFilter}
                            testid="coe-people-role-filter"
                            options={[
                                { value: "all",    label: "All Roles" },
                                { value: "admin",  label: "Admin" },
                                { value: "editor", label: "Editor" },
                                { value: "viewer", label: "Viewer" },
                            ]}
                        />
                        <div className="inline-flex items-center rounded-md border border-border bg-card overflow-hidden h-9" data-testid="coe-people-view-toggle" role="tablist">
                            <button
                                type="button" onClick={() => setViewPersisted("list")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="coe-view-list"
                                aria-pressed={view === "list"}
                                title="List view"
                            >
                                <ListBullets size={13} weight="bold" />
                            </button>
                            <button
                                type="button" onClick={() => setViewPersisted("grid")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="coe-view-grid"
                                aria-pressed={view === "grid"}
                                title="Grid view"
                            >
                                <SquaresFour size={13} weight="bold" />
                            </button>
                        </div>
                    </>
                )}
            />

            <FilterChipRow
                testid="coe-people-chip-row"
                chipTestidPrefix="coe-people-status"
                value={statusChip}
                onChange={setStatusChip}
                chips={[
                    { id: "all",      label: "All",      count: stats.total,    tone: "slate" },
                    { id: "active",   label: "Active",   count: stats.active,   tone: "emerald" },
                    { id: "disabled", label: "Disabled", count: stats.disabled, tone: "rose" },
                ]}
            />

            {/* Section header + Import/Export actions */}
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="font-display text-sm font-bold text-foreground">
                        {statusChip === "disabled" ? "Disabled users" : statusChip === "active" ? "Active users" : "All users"}
                    </h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="coe-people-count">
                        {filtered.length === 0
                            ? "No users match"
                            : `Showing ${Math.min(pageSlice.length, filtered.length)} of ${filtered.length}`}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        onClick={() => setImportOpen(true)}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        data-testid="coe-people-import-btn"
                    >
                        <FileArrowUp size={12} weight="bold" className="mr-1.5" /> Import
                    </Button>
                    <Button
                        onClick={exportCsv}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        data-testid="coe-people-export-btn"
                    >
                        <FileArrowDown size={12} weight="bold" className="mr-1.5" /> Export
                    </Button>
                </div>
            </div>

            {/* iter101.34 — Bulk action bar (visible only when ≥1 row selected) */}
            {selectedIds.size > 0 && (
                <BulkActionBar
                    count={selectedIds.size}
                    onClear={clearSelection}
                    onActivate={() => executeBulk("activate")}
                    onDeactivate={() => executeBulk("deactivate")}
                    onChangeRole={(roleId) => executeBulk("change_role", { custom_role_id: roleId })}
                    roles={roles}
                    bulkRoleId={bulkRoleId}
                    setBulkRoleId={setBulkRoleId}
                    busy={bulkBusy}
                />
            )}

            {/* List / Grid */}
            {pageSlice.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground text-sm" data-testid="coe-people-empty">
                    No users match your filters.
                </div>
            ) : view === "list" ? (
                <PeopleListView
                    filtered={pageSlice}
                    maxOpenLeads={maxOpenLeads}
                    maxOpenCases={maxOpenCases}
                    devMode={devMode}
                    currentUser={currentUser}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onToggleSelectAll={toggleSelectAll}
                    isAllSelected={isAllSelected}
                    onlineIds={onlineIds}
                    onChanged={load}
                />
            ) : (
                <PeopleGridView
                    filtered={pageSlice}
                    devMode={devMode}
                    currentUser={currentUser}
                />
            )}

            {/* Pagination */}
            {filtered.length > PAGE_SIZE && (
                <Pagination
                    page={currentPage}
                    pageCount={pageCount}
                    onChange={setPage}
                    total={filtered.length}
                    pageSize={PAGE_SIZE}
                />
            )}

            <PersonDialog
                open={open}
                onClose={() => setOpen(false)}
                form={form}
                setForm={setForm}
                roles={roles}
                onSave={save}
                saving={saving}
            />

            <ImportUsersDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={load}
            />
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────
   List view (table-like) — dense scanning UX.
   iter101.24 — Redesigned Workload column to be role-appropriate.
   iter101.34 — Added: bulk-select checkbox, online-status dot, split
                Team + Role columns (staff_type · perm tier), Last Login
                column, and a compact Actions cell with Edit / History /
                More (kebab). Removed the standalone Completed column
                (it now surfaces in the More menu).
   iter101.40 — Collapsed "Team" + "Role" into a single "Role" column
                (staff_type label without the legacy "CoE-" prefix; no
                amber chip). Actions cell dropped the "open profile"
                arrow (row already links there) and picked up an
                Enable/Disable icon that uses the same reason-prompt
                dialog as the profile page's danger zone.
   ───────────────────────────────────────────────────────────────────── */
function PeopleListView({
    filtered,
    maxOpenLeads,
    maxOpenCases,
    devMode,
    currentUser,
    selectedIds,
    onToggleSelect,
    onToggleSelectAll,
    isAllSelected,
    onlineIds,
    onChanged,
}) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="coe-people-list">
            <div className="hidden md:grid grid-cols-[28px_2.4fr_2fr_1.6fr_2.2fr_1.1fr_84px] gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                <div>
                    <button
                        type="button"
                        onClick={onToggleSelectAll}
                        className="w-4 h-4 grid place-items-center text-muted-foreground hover:text-primary"
                        aria-label={isAllSelected ? "Deselect all" : "Select all"}
                        data-testid="coe-people-select-all"
                    >
                        {isAllSelected
                            ? <CheckSquare size={14} weight="fill" className="text-primary" />
                            : <Square size={14} />}
                    </button>
                </div>
                <div>User</div>
                <div>Email / Phone</div>
                <div>Role</div>
                <div>Workload (open)</div>
                <div>Last login</div>
                <div className="text-right">Actions</div>
            </div>
            {filtered.map((p) => {
                const selected = selectedIds?.has(p.id);
                const isOnline = onlineIds?.has(p.id);
                const roleName = stripCoePrefix(p.role_label || p.staff_type);
                return (
                    <div
                        key={p.id}
                        className={`relative group border-b border-border last:border-0 ${p.is_active === false ? "opacity-60" : ""} ${selected ? "bg-primary/5" : ""}`}
                        data-testid={`coe-people-row-${p.id}`}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-[28px_2.4fr_2fr_1.6fr_2.2fr_1.1fr_84px] gap-3 px-5 py-3 items-center hover:bg-secondary/30 transition">
                            {/* Bulk-select checkbox */}
                            <div className="hidden md:block">
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); onToggleSelect(p.id); }}
                                    className="w-4 h-4 grid place-items-center text-muted-foreground hover:text-primary"
                                    aria-label={selected ? "Deselect user" : "Select user"}
                                    data-testid={`coe-people-select-${p.id}`}
                                >
                                    {selected
                                        ? <CheckSquare size={14} weight="fill" className="text-primary" />
                                        : <Square size={14} />}
                                </button>
                            </div>

                            {/* User cell */}
                            <Link to={`/admin/coe/people/${p.id}`} className="flex items-center gap-3 min-w-0">
                                <Avatar src={p.avatar_url} name={p.name} size={36} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <div className="font-semibold truncate">{p.name || "Unnamed"}</div>
                                        {p.role === "admin" && <Crown size={12} weight="fill" className="text-amber-600" />}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] mt-0.5">
                                        <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? "bg-emerald-500" : "bg-slate-300"}`} />
                                        <span className={isOnline ? "text-emerald-700 font-semibold" : "text-muted-foreground"}>
                                            {isOnline ? "Online now" : "Offline"}
                                        </span>
                                        {p.is_active === false && (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase font-bold tracking-wider">Disabled</span>
                                        )}
                                    </div>
                                </div>
                            </Link>

                            {/* Email / Phone */}
                            <Link to={`/admin/coe/people/${p.id}`} className="hidden md:flex flex-col text-xs text-muted-foreground min-w-0">
                                <span className="flex items-center gap-1.5 truncate"><Phone size={11} /> {p.phone || "—"}</span>
                                {p.email && <span className="flex items-center gap-1.5 truncate mt-0.5"><Envelope size={11} /> {p.email}</span>}
                            </Link>

                            {/* Role — merged Team + Role column (iter101.40) */}
                            <Link
                                to={`/admin/coe/people/${p.id}`}
                                className="hidden md:flex text-xs text-foreground min-w-0 items-center"
                                data-testid={`coe-people-role-${p.id}`}
                            >
                                <span className="font-medium truncate" title={roleName}>{roleName || "—"}</span>
                            </Link>

                            {/* Workload */}
                            <Link to={`/admin/coe/people/${p.id}`} className="min-w-0">
                                <WorkloadInline p={p} maxLeads={maxOpenLeads} maxCases={maxOpenCases} />
                            </Link>

                            {/* Last login */}
                            <Link to={`/admin/coe/people/${p.id}`} className="hidden md:block text-xs text-muted-foreground">
                                {formatRelative(p.last_login_at)}
                            </Link>

                            {/* Actions — iter101.40 · Enable/Disable + Impersonate.
                                Dropped the standalone "open profile" arrow because
                                every cell in the row is already a link to the same
                                profile page. */}
                            <div className="hidden md:flex items-center justify-end gap-0.5">
                                <RowActions
                                    target={p}
                                    currentUser={currentUser}
                                    devMode={devMode}
                                    onChanged={onChanged}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


/* iter101.40 — Row-level action cluster. Renders an Enable/Disable
   icon (state-aware: rose UserMinus when active, emerald UserPlus when
   disabled) that opens the same reason-prompt dialog the profile page
   uses; and the DEV-only Impersonate button. Prevents the enable/disable
   icon from firing on the user's OWN row so an admin can't lock themselves
   out with one click. */
function RowActions({ target, currentUser, devMode, onChanged }) {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const isActive = target.is_active !== false;
    const isSelf = target.id === currentUser?.id;

    const doToggle = async (comment) => {
        try {
            await api.put(`/admin/coe/people/${target.id}/toggle`, { comment });
            toast.success(isActive ? `${target.name} deactivated` : `${target.name} activated`);
            setDialogOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
            throw e;
        }
    };

    return (
        <>
            {!isSelf && (
                <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDialogOpen(true); }}
                    title={isActive ? `Deactivate ${target.name}` : `Activate ${target.name}`}
                    aria-label={isActive ? `Deactivate ${target.name}` : `Activate ${target.name}`}
                    data-testid={`coe-people-toggle-${target.id}`}
                    className={`w-7 h-7 grid place-items-center rounded-md transition ${
                        isActive
                            ? "text-rose-700 hover:text-rose-900 hover:bg-rose-100"
                            : "text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
                    }`}
                >
                    {isActive ? <UserMinus size={13} weight="bold" /> : <UserPlus size={13} weight="bold" />}
                </button>
            )}
            {devMode && isActive && !isSelf && (
                <ImpersonateButton target={target} currentUser={currentUser} />
            )}
            <CommentRequiredDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                title={isActive ? `Deactivate ${target.name}?` : `Activate ${target.name}?`}
                description={isActive
                    ? "They will lose access immediately. All their work history and lead ownership are preserved. Please note the reason for deactivation."
                    : "They will regain access immediately and can resume work. Please note the reason for reactivation."}
                ctaLabel={isActive ? "Deactivate user" : "Activate user"}
                ctaVariant={isActive ? "destructive" : "default"}
                onConfirm={doToggle}
            />
        </>
    );
}

/* iter101.34 — Compact role pill (Admin amber · Editor emerald · Viewer slate).
   Custom role name surfaces underneath as a small caption. */
function RolePill({ tier, customRoleName }) {
    const cfg = tier === "admin"
        ? { label: "Admin",  cls: "bg-amber-100 text-amber-800" }
        : tier === "editor"
            ? { label: "Editor", cls: "bg-emerald-100 text-emerald-800" }
            : { label: "Viewer", cls: "bg-slate-100 text-slate-700" };
    return (
        <div className="min-w-0">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${cfg.cls} font-bold uppercase tracking-wider text-[10px]`}>
                {cfg.label}
            </span>
            {customRoleName && (
                <div className="text-[9px] text-muted-foreground truncate mt-0.5" title={customRoleName}>
                    {customRoleName}
                </div>
            )}
        </div>
    );
}

/* iter101.34 — Turn an ISO timestamp into a mockup-style relative string.
   "Today 3:00 PM" · "Yesterday 5:45 PM" · "3 days ago" · "12 Jul". */
function formatRelative(iso) {
    if (!iso) return <span className="italic">Never</span>;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return <span className="italic">—</span>;
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startYesterday = new Date(startToday.getTime() - 86_400_000);
    const time = d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
    if (d >= startToday) return `Today ${time}`;
    if (d >= startYesterday) return `Yesterday ${time}`;
    const diffDays = Math.floor((startToday - d.getTime()) / 86_400_000);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

/* ─────────────────────────────────────────────────────────────────────
   Grid view — provider-style cards (large avatar, name, role chip,
   workload pills). Mirrors the doctor directory cards.
   ───────────────────────────────────────────────────────────────────── */
function PeopleGridView({ filtered, devMode, currentUser }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coe-people-grid">
            {filtered.map((p) => {
                const vis = STAFF_TYPE_VIS[p.staff_type] || { Icon: Users, tone: "primary" };
                const t = TONE_CLS[vis.tone] || TONE_CLS.primary;
                return (
                    <div key={p.id} className="relative group">
                        <Link
                            to={`/admin/coe/people/${p.id}`}
                            className={`block rounded-2xl border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-primary/40 transition-all ${p.is_active === false ? "opacity-70 border-rose-200" : "border-border"}`}
                            data-testid={`coe-people-card-${p.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <Avatar src={p.avatar_url} name={p.name} size={56} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1 flex-wrap">
                                        <div className="font-semibold truncate text-sm" title={p.name}>{p.name || "Unnamed"}</div>
                                        {p.role === "admin" && <Crown size={11} weight="fill" className="text-amber-600 shrink-0" />}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5 truncate">
                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${t.bg} ${t.text} font-bold uppercase tracking-wide text-[9px]`}>
                                            <vis.Icon size={9} weight="fill" /> {p.role_label || p.staff_type}
                                        </span>
                                        {p.is_active === false && (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase font-bold tracking-wider">Off</span>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight size={14} className="text-muted-foreground shrink-0 opacity-60 group-hover:translate-x-0.5 group-hover:opacity-100 transition-all" />
                            </div>
                            <div className="mt-3 pt-3 border-t border-border space-y-1.5 text-xs text-muted-foreground">
                                {p.phone && <div className="flex items-center gap-1.5 truncate"><Phone size={11} /> {p.phone}</div>}
                                {p.email && <div className="flex items-center gap-1.5 truncate"><Envelope size={11} /> {p.email}</div>}
                            </div>
                            <div className="mt-3 flex items-center gap-3 text-[11px] tabular-nums">
                                <Pill dot="sky" value={p.open_web} label="web" />
                                <Pill dot="violet" value={p.open_b2b} label="b2b" />
                                <Pill dot="amber" value={p.open_application} label="apps" />
                                <span className="ml-auto font-bold text-foreground">{p.open_leads_count || 0} open</span>
                            </div>
                        </Link>
                        {devMode && p.is_active !== false && p.id !== currentUser?.id && (
                            <ImpersonateButton
                                target={p}
                                currentUser={currentUser}
                                className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────
   ImpersonateButton — DEV-only "Test login as this user" CTA on each
   row/card. Stops the click from bubbling to the parent <Link> nav.
   iter101.23 — Collapsed from a big amber pill to a compact icon-only
   button so it doesn't shout in the row. Tooltip on hover retains
   discoverability.
   iter102.12 — Switched to the NEW-TAB flow (same as Admin doctor/
   patient rows) so the admin's own session on the original tab stays
   intact. Previously CoE impersonation swapped tokens in the same tab,
   forcing the admin through the exit-impersonation banner to get back.
   ───────────────────────────────────────────────────────────────────── */
function ImpersonateButton({ target, currentUser, className = "" }) {
    // `currentUser` is unused now — the new-tab flow doesn't need to
    // stash the admin's identity because localStorage is preserved.
    // Kept in the signature for callsite compatibility.
    void currentUser;
    const onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openImpersonationInNewTab({
            targetUserId: target.id,
            targetName: target.name,
        });
    };
    return (
        <button
            type="button"
            onClick={onClick}
            title={`Open ${target.name}'s portal in a new tab (dev mode)`}
            aria-label={`Test login as ${target.name}`}
            data-testid={`impersonate-${target.id}`}
            className={`w-7 h-7 grid place-items-center rounded-md text-amber-700 hover:text-amber-900 hover:bg-amber-100 transition ${className}`}
        >
            <SignIn size={13} weight="bold" />
        </button>
    );
}

function Pill({ dot, value, label }) {
    const cls = dot === "sky" ? "bg-sky-500" : dot === "violet" ? "bg-violet-500" : "bg-amber-500";
    return (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
            <span className={`w-1.5 h-1.5 rounded-full ${cls}`} />
            <span className="font-medium text-foreground">{value || 0}</span> {label}
        </span>
    );
}

/* ---------- Avatar ---------- */
function Avatar({ src, name, size = 40 }) {
    const [errored, setErrored] = React.useState(false);
    const init = initials(name);
    const px = `${size}px`;
    if (src && !errored) {
        return (
            <img
                src={src}
                alt={name || "User"}
                onError={() => setErrored(true)}
                className="rounded-full object-cover ring-1 ring-border shrink-0 bg-secondary"
                style={{ width: px, height: px }}
                data-testid="coe-avatar-img"
            />
        );
    }
    return (
        <div
            className="rounded-full bg-primary/10 text-primary font-semibold grid place-items-center shrink-0"
            style={{ width: px, height: px, fontSize: `${Math.max(11, size * 0.34)}px` }}
            data-testid="coe-avatar-initials"
        >
            {init}
        </div>
    );
}

/* ---------- WorkloadInline (role-aware workload for list rows) ----------
 * iter101.24 — For lead-facing roles (counsellor / sales / coe_lead)
 * we render the stacked leads bar (web / b2b / applications).
 * For case-facing roles (planner / qa / technician / production) we
 * render a case-count bar with role-appropriate splits so admin sees
 * a planner's queue instead of "No open leads".
 * For staff types not tied to either (support, course_creator) we
 * render "—" to be honest instead of misleading. */
const LEADS_ROLES = new Set(["counsellor", "sales", "coe_lead"]);
const CASE_ROLES = new Set(["planner", "qa", "technician", "production"]);

function WorkloadInline({ p, maxLeads, maxCases }) {
    const staffType = (p.staff_type || "").toLowerCase();
    const isAdmin = p.role === "admin";

    // Admins see the union of all leads + cases (they're catch-alls)
    if (isAdmin) {
        const leads = p.open_leads_count || 0;
        const cases = p.open_cases_count || 0;
        if (leads === 0 && cases === 0) {
            return <span className="text-xs text-muted-foreground">No open items</span>;
        }
        return (
            <div className="text-[11px] tabular-nums text-muted-foreground flex items-center gap-3">
                {leads > 0 && <span><span className="w-1.5 h-1.5 rounded-full bg-sky-500 inline-block mr-1" /><span className="font-semibold text-foreground">{leads}</span> leads</span>}
                {cases > 0 && <span><span className="w-1.5 h-1.5 rounded-full bg-primary inline-block mr-1" /><span className="font-semibold text-foreground">{cases}</span> cases</span>}
            </div>
        );
    }

    if (LEADS_ROLES.has(staffType)) {
        const w = p.open_web || 0, b = p.open_b2b || 0, a = p.open_application || 0;
        const total = p.open_leads_count || 0;
        if (total === 0) {
            return <span className="text-xs text-muted-foreground">No open leads</span>;
        }
        const denom = Math.max(1, maxLeads);
        return (
            <div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden flex" role="img"
                    aria-label={`${total} open: ${w} web, ${b} b2b, ${a} applications`}>
                    <div className="h-full bg-sky-500" style={{ width: `${(w / denom) * 100}%` }} />
                    <div className="h-full bg-violet-500" style={{ width: `${(b / denom) * 100}%` }} />
                    <div className="h-full bg-amber-500" style={{ width: `${(a / denom) * 100}%` }} />
                </div>
                <div className="flex items-center gap-2.5 text-[10px] text-muted-foreground mt-1 tabular-nums">
                    <span className="font-semibold text-foreground">{total} leads</span>
                    {w > 0 && <span><span className="w-1.5 h-1.5 rounded-full bg-sky-500 inline-block mr-1" />{w} web</span>}
                    {b > 0 && <span><span className="w-1.5 h-1.5 rounded-full bg-violet-500 inline-block mr-1" />{b} b2b</span>}
                    {a > 0 && <span><span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block mr-1" />{a} app</span>}
                </div>
            </div>
        );
    }

    if (CASE_ROLES.has(staffType)) {
        // Show the specific bucket for this role first, then the others
        const roleCount = staffType === "planner" ? (p.open_cases_planner || 0)
            : staffType === "qa" ? (p.open_cases_qa || 0)
            : staffType === "technician" ? (p.open_cases_technician || 0)
            : (p.open_cases_production || 0);
        const total = p.open_cases_count || 0;
        if (total === 0) {
            return <span className="text-xs text-muted-foreground">No open cases</span>;
        }
        const denom = Math.max(1, maxCases);
        const roleLabel = staffType === "planner" ? "planning"
            : staffType === "qa" ? "QA"
            : staffType === "technician" ? "tech"
            : "prod";
        return (
            <div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${(roleCount / denom) * 100}%` }} />
                </div>
                <div className="flex items-center gap-2.5 text-[10px] text-muted-foreground mt-1 tabular-nums">
                    <span className="font-semibold text-foreground">{roleCount} {roleLabel}</span>
                    {total > roleCount && <span>· {total - roleCount} other</span>}
                </div>
            </div>
        );
    }

    // Unmapped staff types (support, course_creator, etc.) — be honest.
    return <span className="text-xs text-muted-foreground">—</span>;
}

/* ---------- UserOverviewCard (unused as of iter101.34 — kept for possible future rollback of the KPI-strip refactor) ---------- */
function UserOverviewCard({ label, value, sub, Icon, tone, active, onClick, testid }) {
    const t = TONE_CLS[tone] || TONE_CLS.primary;
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            title={`Show ${label.toLowerCase()}`}
            className={`text-left rounded-xl bg-card border p-3 transition-all hover:shadow-sm hover:-translate-y-px ${active ? "border-primary ring-2 ring-primary/20" : "border-border"}`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <div className={`w-7 h-7 rounded-lg ${t.bg} ${t.text} grid place-items-center`}>
                    <Icon size={13} weight="fill" />
                </div>
                <span className={`w-2 h-2 rounded-full ${tone === "rose" ? "bg-rose-400" : tone === "emerald" ? "bg-emerald-500" : "bg-primary"}`} />
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground mt-1 truncate">{label}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
        </button>
    );
}

/* ---------- StaffTypeCard (clickable filter card per staff_type) ---------- */
function StaffTypeCard({ data, active, onClick }) {
    const vis = STAFF_TYPE_VIS[data.key] || { Icon: Users, tone: "primary" };
    const t = TONE_CLS[vis.tone] || TONE_CLS.primary;
    const headerNum = data.active;
    const displayLabel = data.short_label || data.label;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-xl bg-card border p-3 transition-all hover:shadow-sm hover:-translate-y-px ${active ? "border-primary ring-2 ring-primary/20" : "border-border"}`}
            data-testid={`coe-card-${data.key}`}
            title={`${data.label}: ${data.active} active · ${data.disabled} disabled`}
        >
            <div className="flex items-center justify-between mb-1.5">
                <div className={`w-7 h-7 rounded-lg ${t.bg} ${t.text} grid place-items-center`}>
                    <vis.Icon size={13} weight="fill" />
                </div>
                <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-[10px] font-bold text-emerald-700 tabular-nums">{data.active}</span>
                    {data.disabled > 0 && (
                        <>
                            <span className="text-muted-foreground mx-0.5">·</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            <span className="text-[10px] font-bold text-muted-foreground tabular-nums">{data.disabled}</span>
                        </>
                    )}
                </div>
            </div>
            <div className="font-display text-2xl font-bold tabular-nums leading-none">{headerNum}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold mt-1 truncate" title={data.label}>
                {displayLabel}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={data.label}>
                {data.label !== displayLabel ? data.label : data.description}
            </div>
        </button>
    );
}

/* ---------- FilterPill (small inline filter chip above the COE cards row) ---------- */
function FilterPill({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 h-7 rounded-full border inline-flex items-center gap-1 transition ${
                active
                    ? "bg-foreground text-white border-foreground"
                    : "bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
            }`}
            data-testid={testid}
        >
            {children}
        </button>
    );
}

function PersonDialog({ open, onClose, form, setForm, roles, onSave, saving }) {
    const builtIn = roles.filter((r) => r.is_built_in);
    const custom = roles.filter((r) => !r.is_built_in);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="coe-person-dialog">
                <DialogHeader>
                    <DialogTitle>Add User</DialogTitle>
                    <DialogDescription>
                        For new team members, prefer sending an invite link from the Invites tab. Direct-add bypasses onboarding.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Field label="Name" required>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coe-person-name" />
                    </Field>
                    <Field label="Phone" required>
                        <PhoneInput10 value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="coe-person-phone" />
                    </Field>
                    <Field label="Email">
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coe-person-email" />
                    </Field>
                    <Field label="Built-in role">
                        <select value={form.staff_type} onChange={(e) => setForm({ ...form, staff_type: e.target.value })}
                            className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            data-testid="coe-person-staff-type">
                            {builtIn.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                        </select>
                    </Field>
                    {custom.length > 0 && (
                        <Field label="Custom role (overrides built-in permissions)">
                            <select value={form.custom_role_id || ""} onChange={(e) => setForm({ ...form, custom_role_id: e.target.value })}
                                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                                data-testid="coe-person-custom-role">
                                <option value="">— None (use built-in role) —</option>
                                {custom.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </Field>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={onSave} disabled={saving} data-testid="coe-person-save">
                        {saving ? "Saving…" : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                {label} {required && <span className="text-rose-600">*</span>}
            </Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}


/* ============================================================================
   iter101.34 — NEW HELPERS for the mockup-inspired Team & Access retrofit.
   ========================================================================== */

/* Compact dropdown that matches the FilterSearchBar's h-9 canonical grammar.
   Used for Team / Role / Status in the rightSlot. */
function FilterSelect({ value, onChange, options, testid }) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="appearance-none h-9 pl-3 pr-8 rounded-full bg-secondary/40 border border-border text-[12px] font-medium text-foreground focus:bg-card focus:outline-none min-w-[110px]"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            <CaretDown size={10} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
    );
}

/* Bulk-action bar — appears above the list when ≥1 row is selected. Offers
   Activate / Deactivate / Change role. Backend enforces auth + skips
   self-mutating actions (an admin can't deactivate themselves). */
function BulkActionBar({
    count,
    onClear,
    onActivate,
    onDeactivate,
    onChangeRole,
    roles,
    bulkRoleId,
    setBulkRoleId,
    busy,
}) {
    const [mode, setMode] = useState(null); // null | "change_role"
    return (
        <div
            className="bg-primary/5 border border-primary/30 rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap"
            data-testid="coe-people-bulk-bar"
        >
            <span className="text-xs font-bold text-primary">
                {count} user{count === 1 ? "" : "s"} selected
            </span>
            <span className="text-border">·</span>
            <button
                type="button"
                onClick={onActivate}
                disabled={busy}
                className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="coe-bulk-activate"
            >
                <UserPlus size={12} weight="bold" /> Activate
            </button>
            <button
                type="button"
                onClick={onDeactivate}
                disabled={busy}
                className="text-[11px] font-semibold text-rose-700 hover:text-rose-900 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="coe-bulk-deactivate"
            >
                <UserMinus size={12} weight="bold" /> Deactivate
            </button>
            {mode === "change_role" ? (
                <div className="inline-flex items-center gap-2">
                    <select
                        value={bulkRoleId}
                        onChange={(e) => setBulkRoleId(e.target.value)}
                        className="h-7 px-2 rounded-md border border-border bg-card text-[11px]"
                        data-testid="coe-bulk-role-select"
                    >
                        <option value="">— Pick a role —</option>
                        {roles.filter((r) => !r.is_built_in).map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                    <Button
                        size="sm"
                        className="h-7 px-3 rounded-full"
                        disabled={busy || !bulkRoleId}
                        onClick={() => { onChangeRole(bulkRoleId); setMode(null); }}
                        data-testid="coe-bulk-role-apply"
                    >
                        Apply
                    </Button>
                    <button
                        type="button"
                        onClick={() => setMode(null)}
                        className="text-[11px] text-muted-foreground hover:text-foreground"
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setMode("change_role")}
                    disabled={busy}
                    className="text-[11px] font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="coe-bulk-change-role"
                >
                    <ShieldWarning size={12} weight="bold" /> Change role
                </button>
            )}
            <button
                type="button"
                onClick={onClear}
                className="ml-auto text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="coe-bulk-clear"
            >
                <X size={11} weight="bold" /> Clear
            </button>
        </div>
    );
}

/* Simple client-side pagination. Server-side is overkill at the 500-user cap. */
function Pagination({ page, pageCount, onChange, total, pageSize }) {
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
        <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-3" data-testid="coe-people-pagination">
            <div>Showing {from}–{to} of {total}</div>
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => onChange(page - 1)}
                    className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md border border-border bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="coe-people-page-prev"
                >
                    <CaretLeft size={11} weight="bold" /> Previous
                </button>
                <span className="tabular-nums font-semibold text-foreground px-2">
                    {page} / {pageCount}
                </span>
                <button
                    type="button"
                    disabled={page === pageCount}
                    onClick={() => onChange(page + 1)}
                    className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md border border-border bg-card hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="coe-people-page-next"
                >
                    Next <CaretRight size={11} weight="bold" />
                </button>
            </div>
        </div>
    );
}

/* Import Users CSV dialog. Two-phase flow: upload → preview → commit.
   Mirrors the Patient Bulk Import UX (iter101.18) so operators only
   have to learn one pattern. */
function ImportUsersDialog({ open, onClose, onImported }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState("upload"); // upload | preview

    const reset = () => { setFile(null); setPreview(null); setStep("upload"); };
    const handleClose = () => { reset(); onClose(); };

    const onFilePicked = async (f) => {
        if (!f) return;
        setFile(f);
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await api.post("/admin/coe/people/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data);
            setStep("preview");
        } catch (e) {
            toast.error(e.response?.data?.detail || "CSV preview failed");
        }
        setBusy(false);
    };

    const commit = async () => {
        if (!preview) return;
        setBusy(true);
        try {
            const r = await api.post("/admin/coe/people/import/commit", { rows: preview.rows });
            toast.success(`${r.data.imported} users imported`);
            onImported?.();
            handleClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        }
        setBusy(false);
    };

    const downloadTemplate = () => {
        const csv = "name,phone,email,staff_type\nJane Doe,9876543210,jane@example.com,counsellor\n";
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "coe-users-template.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="coe-import-dialog">
                <DialogHeader>
                    <DialogTitle>Import users</DialogTitle>
                    <DialogDescription>
                        Bulk-add COE staff from a CSV. Required columns: <code>name</code>, <code>phone</code>. Optional: <code>email</code>, <code>staff_type</code>.
                    </DialogDescription>
                </DialogHeader>

                {step === "upload" && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Not sure of the shape?</span>
                            <button
                                type="button"
                                onClick={downloadTemplate}
                                className="text-primary hover:underline font-semibold inline-flex items-center gap-1"
                                data-testid="coe-import-template"
                            >
                                <DownloadSimple size={11} weight="bold" /> Download template
                            </button>
                        </div>
                        <label
                            className="block border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary hover:bg-primary/5 cursor-pointer transition"
                            data-testid="coe-import-dropzone"
                        >
                            <FileArrowUp size={28} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
                            <div className="text-sm font-semibold">Drop CSV or click to browse</div>
                            <div className="text-[11px] text-muted-foreground mt-1">Max 2MB · UTF-8 encoded</div>
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                className="hidden"
                                onChange={(e) => onFilePicked(e.target.files?.[0])}
                                data-testid="coe-import-file-input"
                            />
                        </label>
                    </div>
                )}

                {step === "preview" && preview && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-4 gap-2 text-xs">
                            <StatChip label="Total rows"   value={preview.total_rows}    tone="slate" testid="coe-import-stat-total" />
                            <StatChip label="Ready"        value={preview.valid_count}   tone="emerald" testid="coe-import-stat-valid" />
                            <StatChip label="Duplicates"   value={preview.duplicate_count} tone="amber" testid="coe-import-stat-dup" />
                            <StatChip label="Invalid"      value={preview.invalid_count} tone="rose" testid="coe-import-stat-invalid" />
                        </div>
                        <div className="max-h-60 overflow-y-auto rounded-xl border border-border bg-card">
                            <table className="w-full text-xs">
                                <thead className="bg-secondary/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-3 py-2">Status</th>
                                        <th className="text-left px-3 py-2">Name</th>
                                        <th className="text-left px-3 py-2">Phone</th>
                                        <th className="text-left px-3 py-2">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.rows || []).slice(0, 50).map((r, i) => (
                                        <tr key={i} className="border-b border-border last:border-0">
                                            <td className="px-3 py-1.5">
                                                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                                                    r.status === "valid"     ? "bg-emerald-100 text-emerald-700"
                                                    : r.status === "duplicate" ? "bg-amber-100 text-amber-700"
                                                    : "bg-rose-100 text-rose-700"
                                                }`}>
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td className="px-3 py-1.5">{r.name || "—"}</td>
                                            <td className="px-3 py-1.5 font-mono text-[11px]">{r.phone || "—"}</td>
                                            <td className="px-3 py-1.5 text-muted-foreground">{r.note || ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={busy}>Cancel</Button>
                    {step === "preview" && (
                        <Button
                            onClick={commit}
                            disabled={busy || (preview?.valid_count || 0) === 0}
                            data-testid="coe-import-commit"
                        >
                            {busy ? "Importing…" : `Import ${preview?.valid_count || 0}`}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StatChip({ label, value, tone, testid }) {
    const cls = tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
        : tone === "rose" ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-slate-50 text-slate-700 border-slate-200";
    return (
        <div className={`border rounded-md px-2 py-1.5 ${cls}`} data-testid={testid}>
            <div className="text-[9px] uppercase tracking-wider font-bold">{label}</div>
            <div className="font-display font-bold tabular-nums">{value}</div>
        </div>
    );
}
